import {
    ToggleGridColumnSortAction,
    ToggleGridSingleColumnSortAction,
} from './actions';
import { WidgetActionType } from 'widgets/store/actions';
import { Reducer } from 'redux';
import { WidgetType } from 'widgets/WidgetType';

import { SortModel, SortDirection } from '@instinet-analytics/core-lib';
import { GridSortState } from 'widgets/store/grid/sorting/GridSortState';
import { WidgetSetConfigAction } from 'widgets/store/configuration/actions';
import { GridVisibleColumnState } from 'widgets/store/grid/visibleColumns/GridVisibleColumnState';

type HandledActions<
    TColumnId extends string,
    TSortState extends GridSortState<TColumnId>,
> =
    | ToggleGridColumnSortAction<TColumnId>
    | ToggleGridSingleColumnSortAction<TColumnId>
    | WidgetSetConfigAction<TSortState>;

export const createGridColumnSortReducer = <
    TColumnId extends string,
    TSortState extends GridSortState<TColumnId>,
>(
    widgetType: WidgetType,
    initialState: TSortState
): Reducer<TSortState, HandledActions<TColumnId, TSortState>> => {
    return (
        state = initialState,
        action: HandledActions<TColumnId, TSortState>
    ) => {
        if (!action.payload || !action.payload.widgetType) {
            return state;
        }
        if (action.payload.widgetType !== widgetType) {
            return state;
        }
        switch (action.type) {
            case WidgetActionType.WIDGET_TOGGLE_GRID_COLUMN_SORT:
                return {
                    ...state,
                    sort: applySortToggle(
                        state.sort,
                        action.payload.columnId,
                        action.payload.defaultSortDesc
                    ),
                };

            case WidgetActionType.WIDGET_TOGGLE_GRID_SINGLE_COLUMN_SORT:
                return {
                    ...state,
                    sort: applySingleSortToggle(
                        state.sort,
                        action.payload.columnId,
                        action.payload.defaultSortDesc
                    ),
                };

            case WidgetActionType.WIDGET_SET_CONFIG:
                return removeHiddenColumnsFromSort(state, action);
            default:
                return state;
        }
    };
};

export function applySortToggle<T extends string>(
    currentSort: SortModel<T>,
    colId: T,
    startDescending = false
): SortModel<T> {
    const initialSort = startDescending
        ? SortDirection.DESCENDING
        : SortDirection.ASCENDING;
    if (!currentSort.some((x) => x.colId === colId)) {
        return [...currentSort, { colId, sort: initialSort }];
    }

    return currentSort.reduce<SortModel<T>>((prev, columnSort) => {
        if (columnSort.colId !== colId) {
            prev.push(columnSort);
        } else {
            if (columnSort.sort === initialSort) {
                prev.push({
                    ...columnSort,
                    sort: startDescending
                        ? SortDirection.ASCENDING
                        : SortDirection.DESCENDING,
                });
            }
        }
        return prev;
    }, []);
}

export function applySingleSortToggle<T extends string>(
    currentSort: SortModel<T>,
    colId: T,
    startDescending = false
): SortModel<T> {
    const initialSort = startDescending
        ? SortDirection.DESCENDING
        : SortDirection.ASCENDING;

    const columnSort = currentSort.find((s) => s.colId === colId);

    if (!columnSort) {
        return [{ colId, sort: initialSort }];
    }

    return [
        {
            ...columnSort,
            sort:
                currentSort.length > 1
                    ? // If we had multiple sort columns, we retain the existing sort order...
                      columnSort.sort
                    : // Otherwise we toggle...
                    columnSort.sort === SortDirection.DESCENDING
                    ? SortDirection.ASCENDING
                    : SortDirection.DESCENDING,
        },
    ];
}

function removeHiddenColumnsFromSort<
    TColumnId extends string,
    TSortState extends GridSortState<TColumnId>,
>(state: TSortState, action: WidgetSetConfigAction<TSortState>): TSortState {
    const updateVisibleColumnsAction = action as WidgetSetConfigAction<
        GridVisibleColumnState<TColumnId>
    >;
    const updatedVisibleColumns =
        updateVisibleColumnsAction.payload?.configDelta?.visibleColumns;
    if (updatedVisibleColumns) {
        state.sort = [
            ...state.sort.filter((x) =>
                updatedVisibleColumns.includes(x.colId)
            ),
        ];
    }
    return state;
}
